import * as T from '@radix-ui/react-tabs'
import { useEffect, useRef, type ComponentProps, type FocusEvent } from 'react'
import { cn } from '@/lib/utils'

export const Tabs = T.Root

type TabsListProps = ComponentProps<typeof T.List> & { size?: 'default' | 'sm' }

export function TabsList({ className, size = 'default', ...props }: TabsListProps) {
  return (
    <T.List
      data-size={size}
      className={cn(
        'group/tabs flex w-fit max-w-full items-center gap-0.5 overflow-x-auto overscroll-x-contain rounded-md border border-primary/15 bg-primary/[0.05] p-1 dark:border-primary/20 dark:bg-primary/10 data-[size=sm]:p-0.5 scrollbar-none',
        className,
      )}
      {...props}
    />
  )
}

function centerInList(el: HTMLElement, smooth: boolean) {
  const list = el.parentElement
  if (!list || list.scrollWidth <= list.clientWidth) return
  const lr = list.getBoundingClientRect()
  const er = el.getBoundingClientRect()
  if (er.left >= lr.left && er.right <= lr.right) return
  list.scrollBy({ left: er.left - lr.left - (lr.width - er.width) / 2, behavior: smooth ? 'smooth' : 'auto' })
}

export function TabsTrigger({ className, onFocus, ref, ...props }: ComponentProps<typeof T.Trigger>) {
  const own = useRef<HTMLButtonElement | null>(null)
  const wasActive = useRef(false)
  const mounted = useRef(false)

  useEffect(() => {
    const el = own.current
    if (!el) return
    const active = el.dataset.state === 'active'
    if (active && !wasActive.current) centerInList(el, mounted.current)
    wasActive.current = active
    mounted.current = true
  })

  const setRef = (node: HTMLButtonElement | null) => {
    own.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) ref.current = node
  }

  const reveal = (e: FocusEvent<HTMLButtonElement>) => {
    centerInList(e.currentTarget, true)
    onFocus?.(e)
  }
  return (
    <T.Trigger
      ref={setRef}
      onFocus={reveal}
      className={cn(
        'inline-flex h-8 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-sm border border-transparent px-3 text-[13px] font-medium text-muted-foreground transition-all duration-150',
        'hover:bg-card/60 hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:border-primary/20 data-[state=active]:bg-card data-[state=active]:font-semibold data-[state=active]:text-primary data-[state=active]:shadow-[0_1px_2px_color-mix(in_oklab,var(--brand-blue)_10%,transparent),0_2px_6px_-2px_color-mix(in_oklab,var(--brand-blue)_18%,transparent)]',
        'dark:data-[state=active]:bg-card dark:data-[state=active]:text-foreground dark:data-[state=active]:shadow-[0_1px_3px_color-mix(in_oklab,var(--black)_50%,transparent)]',
        'group-data-[size=sm]/tabs:h-7 group-data-[size=sm]/tabs:px-2.5 group-data-[size=sm]/tabs:text-xs',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  )
}

export const TabsContent = T.Content
